import type Phaser from 'phaser';

export interface HelpToggleOptions {
  scene: Phaser.Scene;
  /** Anchor for the top-left of the pill, in canvas-buffer px. */
  x: number;
  y: number;
  scale?: number;
  onToggle: () => void;
}

/**
 * Top-left "?" pill that arms help mode. Visual treatment matches the
 * BackToFloorPill (gold left border, dark felt fill, gold caption).
 */
export class HelpToggle {
  private rect: Phaser.GameObjects.Rectangle;
  private text: Phaser.GameObjects.Text;
  private bg: Phaser.GameObjects.Graphics;
  private armed = false;
  private readonly sm: number;

  constructor(opts: HelpToggleOptions) {
    const { scene, x, y, onToggle } = opts;
    const sm = opts.scale ?? 1;
    this.sm = sm;
    const padX = 11 * sm;
    const padY = 5 * sm;

    this.text = scene.add
      .text(x + padX, y + padY, '?', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${Math.round(13 * sm)}px`,
        color: '#d4af37',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    const w = this.text.width + padX * 2;
    const h = this.text.height + padY * 2;

    this.bg = scene.add.graphics();
    this.bg.fillStyle(0x06100c, 0.7);
    this.bg.fillRect(x, y, w, h);
    this.bg.fillStyle(0xd4af37, 1);
    this.bg.fillRect(x, y, 2 * sm, h);
    this.bg.setDepth(-1);

    this.rect = scene.add
      .rectangle(x + w / 2, y + h / 2, w, h, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.rect.on('pointerdown', () => {
      this.armed = !this.armed;
      this.applyArmedState();
      onToggle();
    });
    this.rect.on('pointerover', () => !this.armed && this.text.setColor('#f4d35e'));
    this.rect.on('pointerout', () => !this.armed && this.text.setColor('#d4af37'));
  }

  isArmed() {
    return this.armed;
  }

  setArmed(armed: boolean) {
    this.armed = armed;
    this.applyArmedState();
  }

  destroy() {
    this.rect.destroy();
    this.text.destroy();
    this.bg.destroy();
  }

  private applyArmedState() {
    this.text.setColor(this.armed ? '#0d2c1d' : '#d4af37');
    this.bg.clear();
    if (this.armed) {
      this.bg.fillStyle(0xd4af37, 1);
      this.bg.fillRect(
        this.rect.x - this.rect.width / 2,
        this.rect.y - this.rect.height / 2,
        this.rect.width,
        this.rect.height,
      );
    } else {
      this.bg.fillStyle(0x06100c, 0.7);
      this.bg.fillRect(
        this.rect.x - this.rect.width / 2,
        this.rect.y - this.rect.height / 2,
        this.rect.width,
        this.rect.height,
      );
      this.bg.fillStyle(0xd4af37, 1);
      this.bg.fillRect(
        this.rect.x - this.rect.width / 2,
        this.rect.y - this.rect.height / 2,
        2 * this.sm,
        this.rect.height,
      );
    }
  }
}
